/**
 * Turn a server return address into the hash route this app can actually read.
 *
 * THE PROBLEM (#543, fault B). `App.jsx` mounts a `HashRouter` — "HashRouter, deliberately" — so
 * the router only ever reads the part after `#`. But `server/auth/routes/google.routes.js` hands
 * the browser back at PATHS with query strings:
 *
 *     :117 :128 :134   ${APP_URL}/login?error=oauth_config | oauth_disabled
 *     :150             ${APP_URL}/login?error=oauth_denied
 *     :157             ${APP_URL}/login?error=oauth_state
 *     :194             ${APP_URL}/signup?google=1
 *     :244             ${APP_URL}/login?error=oauth_failed
 *
 * A path never reaches the hash router, and the query goes with it. Every one of those reasons is
 * invisible: the family lands on the default route with no idea what happened, and a first-time
 * Google user lands on a signup page that never learns it is mid-handoff.
 *
 * WHY THE FIX IS HERE AND NOT ON THE SERVER. Production still serves the PATH-routed app at the
 * same `APP_URL`. Rewriting the server's targets to hash routes would fix dev by breaking
 * production. The one server change that was safe — `google.routes.js` ending a SUCCESSFUL callback
 * at `${APP_URL}/#${APP_LANDING_ROUTE}` — was safe precisely because it only ADDS a fragment, which
 * a path router ignores. Going further needs the client to meet the server where it already is.
 *
 * WHAT IT DOES NOT DO. It does not invent routes. Only the paths the server actually redirects to
 * are translated, so a stray URL cannot be turned into navigation, and anything already carrying a
 * fragment is left alone — the server's success path already speaks hash, and re-writing it would
 * be a second opinion about where the family should be.
 */

/**
 * The only paths translated, mapped to the hash route that serves them.
 *
 * An allowlist rather than a general `/x` -> `#/x` rewrite. A general rewrite would hand any URL
 * the power to choose a route, and would silently start translating paths that appear later for
 * unrelated reasons.
 */
export const SERVER_RETURN_PATHS = Object.freeze({
  '/login': '/login',
  '/signup': '/signup',
});

/**
 * Compute the hash route for a server return address, or `null` when there is nothing to do.
 *
 * @param {{pathname: string, search: string, hash: string}} location
 * @returns {string|null} the new hash, without the leading `#`, or null to leave the URL alone
 */
export function hashForServerReturn(location) {
  const { pathname = '/', search = '', hash = '' } = location || {};

  // A fragment already present means somebody has already decided where this goes — the server's
  // own success redirect, or a link inside the app. Never overrule it.
  if (hash && hash !== '#') return null;

  const route = SERVER_RETURN_PATHS[pathname.replace(/\/+$/, '') || '/'];
  if (!route) return null;

  // The query rides along, because it IS the message: `?error=oauth_denied` and `?google=1` are
  // the whole reason this translation exists.
  return `${route}${search || ''}`;
}

/**
 * Apply the translation to the real browser URL, before the router mounts.
 *
 * `history.replaceState` rather than assigning `location.hash`, so the broken address does not
 * stay in the back stack for a family to return to.
 *
 * @param {Window} [win] injectable for tests
 * @returns {boolean} whether anything was rewritten
 */
export function applyServerReturnPath(win = typeof window !== 'undefined' ? window : undefined) {
  if (!win || !win.location) return false;

  const next = hashForServerReturn(win.location);
  if (next === null) return false;

  // Drop the path and query from the visible URL and carry them in the fragment instead, so a
  // reload lands in the same place rather than repeating the translation on a path that is gone.
  const base = `${win.location.origin}/`;
  win.history.replaceState(null, '', `${base}#${next}`);
  return true;
}
